<template>
  <div class="advanced-editor-page">
    <div class="page-header">
      <h1>Advanced Code Editor</h1>
      <p>Monaco Editor with advanced features and content-based language detection</p>
    </div>
    
    <div class="editor-container">
      <div class="editor-controls">
        <div class="control-group">
          <label for="content-input">Content Source:</label>
          <select id="content-input" v-model="selectedSample" @change="loadSample">
            <option value="">Select sample content...</option>
            <option value="json">JSON Configuration</option>
            <option value="xml">XML Document</option>
            <option value="javascript">JavaScript Code</option>
            <option value="css">CSS Stylesheet</option>
          </select>
        </div>
        
        <div class="control-group">
          <label>Detected Language:</label>
          <span class="language-badge">{{ detectedLanguage }}</span>
        </div>
        
        <button @click="formatContent" class="format-button" :disabled="!editorValue">
          <Icon name="symbol-misc" :size="16" />
          Format Document
        </button>
      </div>
      
      <div class="monaco-wrapper">
        <MonacoEditor
          ref="monacoEditorRef"
          v-model="editorValue"
          :language="detectedLanguage"
          :height="500"
          :show-toolbar="true"
          :theme="monacoTheme"
          @update:model-value="onContentChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import Icon from '@/components/atoms/Icon.vue'
import MonacoEditor from '@/components/organisms/MonacoEditor.vue'
import { useTheme } from '@/composables/useTheme'

const monacoEditorRef = ref()
const editorValue = ref('')
const selectedSample = ref('')

// Use app theme for Monaco editor
const { theme } = useTheme()
const monacoTheme = computed(() => theme.value === 'light' ? 'vs' : 'vs-dark')

// Sample content for testing
const sampleContent = {
  json: `{
  "name": "example-project",
  "version": "1.0.0",
  "description": "A sample JSON configuration",
  "dependencies": {
    "vue": "^3.0.0",
    "typescript": "^4.0.0"
  },
  "scripts": {
    "dev": "vite",
    "build": "vite build"
  }
}`,
  xml: `<?xml version="1.0" encoding="UTF-8"?>
<configuration>
<database>
<host>localhost</host>
<port>5432</port>
<name>myapp</name>
<credentials>
<username>admin</username>
<password>secret</password>
</credentials>
</database>
<logging>
<level>INFO</level>
<file>/var/log/app.log</file>
</logging>
</configuration>`,
  javascript: `// Sample JavaScript code
function calculateTotal(items) {
  return items.reduce((sum, item) => {
    return sum + (item.price * item.quantity);
  }, 0);
}

class ShoppingCart {
  constructor() {
    this.items = [];
  }

  addItem(product, quantity = 1) {
    const existingItem = this.items.find(item => item.id === product.id);
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      this.items.push({ ...product, quantity });
    }
  }

  getTotal() {
    return calculateTotal(this.items);
  }
}`,
  css: `/* Sample CSS stylesheet */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1rem;
  transition: transform 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
}`
}

// Content-based language detection
const detectLanguageFromContent = (content: string): string => {
  if (!content.trim()) return 'plaintext'
  
  // JSON detection
  if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
    try {
      JSON.parse(content)
      return 'json'
    } catch {
      // Could be JSONC or malformed JSON
      if (content.includes('//') || content.includes('/*')) {
        return 'jsonc'
      }
    }
  }
  
  // XML detection
  if (content.trim().startsWith('<?xml') || content.trim().startsWith('<') && content.includes('</')) {
    return 'xml'
  }
  
  // JavaScript detection
  if (content.includes('function ') || content.includes('const ') || content.includes('class ') || content.includes('=>')) {
    return 'javascript'
  }
  
  // CSS detection
  if (content.includes('{') && content.includes('}') && content.includes(':') && content.includes(';')) {
    return 'css'
  }
  
  // TypeScript detection
  if (content.includes('interface ') || content.includes('type ') || content.includes(': string') || content.includes(': number')) {
    return 'typescript'
  }
  
  return 'plaintext'
}

const detectedLanguage = computed(() => {
  return detectLanguageFromContent(editorValue.value)
})

const loadSample = () => {
  if (selectedSample.value && sampleContent[selectedSample.value as keyof typeof sampleContent]) {
    editorValue.value = sampleContent[selectedSample.value as keyof typeof sampleContent]
  }
}

const onContentChange = (value: string) => {
  editorValue.value = value
}

const formatContent = async () => {
  if (monacoEditorRef.value && monacoEditorRef.value.formatDocument) {
    await nextTick()
    monacoEditorRef.value.formatDocument()
  }
}
</script>

<style scoped lang="scss">
.advanced-editor-page {
  padding: 24px;
  min-height: calc(100vh - 64px);
  background: var(--vscode-editor-background);
  color: var(--vscode-editor-foreground);
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
  
  h1 {
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: var(--vscode-editor-foreground);
  }
  
  p {
    font-size: 14px;
    color: var(--vscode-descriptionForeground);
    margin: 0;
  }
}

.editor-container {
  max-width: 1200px;
  margin: 0 auto;
}

.editor-controls {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px;
  background: var(--vscode-sideBar-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 6px 6px 0 0;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
  
  label {
    font-size: 13px;
    font-weight: 500;
    color: var(--vscode-editor-foreground);
    white-space: nowrap;
  }
  
  select {
    padding: 4px 8px;
    background: var(--vscode-input-background);
    border: 1px solid var(--vscode-input-border);
    border-radius: 3px;
    color: var(--vscode-input-foreground);
    font-size: 13px;
    min-width: 200px;
    
    &:focus {
      outline: none;
      border-color: var(--vscode-focusBorder);
    }
  }
}

.language-badge {
  background: var(--vscode-badge-background);
  color: var(--vscode-badge-foreground);
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

.format-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--vscode-button-background);
  color: var(--vscode-button-foreground);
  border: none;
  border-radius: 3px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background: var(--vscode-button-hoverBackground);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.monaco-wrapper {
  border: 1px solid var(--vscode-widget-border);
  border-top: none;
  border-radius: 0 0 6px 6px;
  overflow: hidden;
  min-height: 50vh;
  height: auto;
}

@media (max-width: 768px) {
  .advanced-editor-page {
    padding: 16px;
  }
  
  .editor-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .control-group {
    flex-direction: column;
    align-items: stretch;
    
    select {
      min-width: auto;
    }
  }
}
</style>
